import React, { useState, useEffect, useCallback } from "react";
import { Wallet, Plus, PlusCircle, Check, X, Trash2, Eye, Download, Clock, ShieldCheck } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Modal, Input, Select } from "../lib/ui";
import { JournalPreview } from "../components/JournalPreview";
import { useToast } from "../lib/toast";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { rp, PeriodPicker, BranchFilter } from "../components/finance";

const STATUS = {
  pending: { label: "Pending", tone: "warn" },
  approved: { label: "Disetujui", tone: "success" },
  rejected: { label: "Ditolak", tone: "danger" },
};
// Sumber Cash In wajib dipilih — tercatat di dokumen, audit trail, dan jurnal akuntansi.
const TOPUP_SOURCES = [
  "Owner Cash Float",
  "Finance Replenishment",
  "Refund",
  "Returned Cash",
  "Lainnya",
];
const todayStr = () => new Date().toISOString().slice(0, 10);
const thisPeriod = () => new Date().toISOString().slice(0, 7);

export default function PettyCash() {
  const toast = useToast();
  const { user } = useAuth();
  const isOwner = ["OWNER", "SUPER_ADMIN"].includes(user?.role);
  const canApprove = isOwner || user?.role === "BRANCH_MANAGER";
  // Cash In (Top Up) is a daily operational workflow — Cashier records Owner float,
  // Finance replenishment, refunds, and returned cash. Approval stays Manager-only.
  const canTopup = canApprove || user?.role === "BRANCH_STAFF";

  const [branchId, setBranchId] = useState(isOwner ? "" : user?.branch_id || "");
  const [period, setPeriod] = useState(thisPeriod());
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [wallets, setWallets] = useState([]);
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState([]);

  const [modal, setModal] = useState(null); // 'create' | 'topup' | 'detail' | 'reject'
  const [detail, setDetail] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  const effBranch = isOwner ? branchId : user?.branch_id || "";
  const empty = { date: todayStr(), branch_id: effBranch, amount: "", category_key: "", description: "", receipt_photo: null };
  const emptyTop = { date: todayStr(), branch_id: effBranch, amount: "", source: "", note: "" };
  const [form, setForm] = useState(empty);
  const [topForm, setTopForm] = useState(emptyTop);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [
        api.get("/petty-cash/entries", { params: { branch_id: effBranch || undefined, period } }),
        isOwner ? api.get("/petty-cash/wallets") : Promise.resolve({ data: { wallets: [] } }),
        api.get("/petty-cash/categories"),
        effBranch ? api.get("/petty-cash/summary", { params: { branch_id: effBranch, period } }) : Promise.resolve({ data: null }),
      ];
      const [e, w, c, s] = await Promise.all(reqs);
      setEntries(e.data.entries || []);
      setWallets(w.data.wallets || []);
      setCategories(c.data.categories || []);
      setSummary(s.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memuat data");
    } finally {
      setLoading(false);
    }
  }, [effBranch, period, isOwner]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const onReceipt = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Ukuran maksimal 5MB"); return; }
    const r = new FileReader();
    r.onload = () => setForm((p) => ({ ...p, receipt_photo: r.result }));
    r.readAsDataURL(f);
  };

  const openCreate = () => { setForm({ ...empty, branch_id: effBranch, category_key: categories[0]?.key || "" }); setModal("create"); };
  const openTopup = () => { setTopForm({ ...emptyTop, branch_id: effBranch }); setModal("topup"); };

  const saveExpense = async () => {
    if (!form.branch_id) { toast.error("Pilih cabang"); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Jumlah harus lebih dari nol"); return; }
    if (!form.category_key) { toast.error("Pilih kategori"); return; }
    if (!form.description.trim()) { toast.error("Isi deskripsi"); return; }
    setBusy(true);
    try {
      await api.post("/petty-cash/entries", {
        branch_id: form.branch_id, date: form.date, amount: Number(form.amount),
        category_key: form.category_key, description: form.description.trim(), receipt_photo: form.receipt_photo,
      });
      toast.success("Cash Out dicatat (menunggu persetujuan)"); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  const saveTopup = async () => {
    if (!topForm.branch_id) { toast.error("Pilih cabang"); return; }
    if (!topForm.amount || Number(topForm.amount) <= 0) { toast.error("Jumlah harus lebih dari nol"); return; }
    if (!topForm.source) { toast.error("Pilih sumber Cash In"); return; }
    setBusy(true);
    try {
      await api.post("/petty-cash/topups", {
        branch_id: topForm.branch_id, date: topForm.date, amount: Number(topForm.amount),
        source: topForm.source, note: topForm.note || null,
      });
      toast.success("Cash In berhasil dicatat"); setModal(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mencatat Cash In"); }
    finally { setBusy(false); }
  };

  const approve = async (id) => { try { await api.post(`/petty-cash/entries/${id}/approve`); toast.success("Disetujui"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } };
  const doReject = async () => {
    setBusy(true);
    try { await api.post(`/petty-cash/entries/${rejectId}/reject`, { reason: rejectReason || null }); toast.success("Ditolak — saldo dikembalikan"); setModal(null); setRejectReason(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/petty-cash/entries/${id}`); toast.success("Dihapus"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); } };

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/petty-cash/entries/${id}`); setDetail(data); setModal("detail"); }
    catch (e) { toast.error("Gagal memuat detail"); }
  };

  const doExport = async (fmt) => {
    try {
      const { data } = await api.get("/petty-cash/export", { params: { branch_id: effBranch || undefined, period, format: fmt }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = `petty-cash-${period}.${fmt === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal mengekspor"); }
  };

  return (
    <div className="space-y-5" data-testid="petty-cash-page">
      <PageHeader title="Petty Cash Cabang" subtitle="Kas kecil per cabang — pengeluaran approved otomatis masuk Biaya Operasional Finance Lite" icon={Wallet}>
        {isOwner && <BranchFilter value={branchId} onChange={setBranchId} />}
        <PeriodPicker value={period} onChange={setPeriod} />
        {canTopup && <Button variant="outline" onClick={openTopup} data-testid="pc-topup-btn"><PlusCircle size={15} /> Cash In</Button>}
        <Button onClick={openCreate} data-testid="pc-add-btn"><Plus size={15} /> Cash Out</Button>
        {isOwner && <>
          <Button variant="outline" onClick={() => doExport("pdf")} data-testid="pc-export-pdf"><Download size={15} /> PDF</Button>
          <Button variant="outline" onClick={() => doExport("xlsx")} data-testid="pc-export-xlsx"><Download size={15} /> Excel</Button>
        </>}
      </PageHeader>

      {loading ? <div className="flex justify-center py-20"><Spinner /></div> : (
        <>
          {/* Owner wallets overview */}
          {isOwner && wallets.length > 0 && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="pc-wallets">
              {wallets.map((w) => (
                <Card key={w.branch_id} className={`cursor-pointer p-4 transition hover:border-primary ${branchId === w.branch_id ? "border-primary ring-1 ring-primary" : ""}`}
                  onClick={() => setBranchId(branchId === w.branch_id ? "" : w.branch_id)} data-testid={`pc-wallet-${w.branch_id}`}>
                  <p className="text-xs font-semibold text-ink-mute">{w.branch_name}</p>
                  <p className="mt-1 font-heading text-lg font-bold text-ink">{rp(w.current_balance)}</p>
                  {w.pending > 0 && <p className="mt-0.5 text-[11px] text-amber-600">Pending {rp(w.pending)}</p>}
                </Card>
              ))}
            </div>
          )}

          {/* Period summary (single branch) */}
          {summary && (
            <Card className="p-5" data-testid="pc-summary">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Stat label="Saldo Awal" value={rp(summary.beginning_balance)} testid="pc-begin" />
                <Stat label="+ Cash In" value={rp(summary.topup)} tone="success" testid="pc-topup" />
                <Stat label="− Cash Out" value={rp(summary.expenses)} tone="danger" testid="pc-expenses" />
                <Stat label="= Saldo Saat Ini" value={rp(summary.current_balance)} strong testid="pc-current" />
              </div>
              {summary.pending > 0 && <p className="mt-3 flex items-center gap-1.5 text-xs text-amber-600"><Clock size={13} /> Menunggu persetujuan: {rp(summary.pending)}</p>}
            </Card>
          )}

          {/* Entries table */}
          <Card className="overflow-hidden">
            {entries.length === 0 ? (
              <div className="py-16 text-center text-sm text-ink-mute" data-testid="pc-empty">Belum ada transaksi petty cash pada periode ini.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="pc-entries-table">
                  <thead className="bg-ink/5 text-left text-xs text-ink-soft">
                    <tr>
                      <th className="px-4 py-2.5">No. Dokumen</th>
                      <th className="px-3 py-2.5">Tanggal</th>
                      {isOwner && !branchId && <th className="px-3 py-2.5">Cabang</th>}
                      <th className="px-3 py-2.5">Kategori</th>
                      <th className="px-3 py-2.5">Deskripsi</th>
                      <th className="px-3 py-2.5 text-right">Jumlah</th>
                      <th className="px-3 py-2.5">Status</th>
                      <th className="px-4 py-2.5 text-right">Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((r) => {
                      const st = STATUS[r.status] || STATUS.pending;
                      return (
                        <tr key={r.id} className="border-t border-line/60" data-testid={`pc-row-${r.id}`}>
                          <td className="px-4 py-2 font-mono text-xs font-semibold text-ink" data-testid={`pc-doc-${r.id}`}>{r.doc_number}</td>
                          <td className="px-3 py-2 text-ink-soft">{r.date}</td>
                          {isOwner && !branchId && <td className="px-3 py-2 text-ink-soft">{r.branch_name}</td>}
                          <td className="px-3 py-2 text-ink-soft">{r.category_name}</td>
                          <td className="px-3 py-2 text-ink-soft">{r.description}</td>
                          <td className="px-3 py-2 text-right font-semibold text-ink">{rp(r.amount)}</td>
                          <td className="px-3 py-2"><Badge tone={st.tone} data-testid={`pc-status-${r.id}`}>{st.label}</Badge></td>
                          <td className="px-4 py-2">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => openDetail(r.id)} className="rounded-lg p-1.5 text-ink-soft hover:bg-ink/5" title="Detail" data-testid={`pc-view-${r.id}`}><Eye size={14} /></button>
                              {canApprove && r.status === "pending" && (
                                <>
                                  <button onClick={() => approve(r.id)} className="rounded-lg p-1.5 text-emerald-600 hover:bg-emerald-50" title="Setujui" data-testid={`pc-approve-${r.id}`}><Check size={14} /></button>
                                  <button onClick={() => { setRejectId(r.id); setRejectReason(""); setModal("reject"); }} className="rounded-lg p-1.5 text-danger hover:bg-danger-light" title="Tolak" data-testid={`pc-reject-${r.id}`}><X size={14} /></button>
                                </>
                              )}
                              {r.status === "pending" && (isOwner || r.created_by === user?.id) && (
                                <button onClick={() => del(r.id)} className="rounded-lg p-1.5 text-ink-mute hover:bg-ink/5" title="Hapus" data-testid={`pc-del-${r.id}`}><Trash2 size={14} /></button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      {/* Create modal */}
      <Modal open={modal === "create"} onClose={() => setModal(null)} title="Tambah Pengeluaran Petty Cash">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Tanggal" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="pc-form-date" />
            <Input label="Jumlah (Rp)" type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pc-form-amount" />
          </div>
          {isOwner && (
            <Select label="Cabang" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} data-testid="pc-form-branch">
              <option value="">Pilih cabang</option>
              {wallets.map((w) => <option key={w.branch_id} value={w.branch_id}>{w.branch_name}</option>)}
            </Select>
          )}
          <Select label="Kategori" value={form.category_key} onChange={(e) => setForm({ ...form, category_key: e.target.value })} data-testid="pc-form-category">
            <option value="">Pilih kategori</option>
            {categories.map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
          </Select>
          <Input label="Deskripsi" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pc-form-desc" />
          <div>
            <label className="mb-1 block text-xs font-semibold text-ink-soft">Foto Struk (opsional)</label>
            {form.receipt_photo ? (
              <div className="relative h-28 w-28 overflow-hidden rounded-xl">
                <img src={form.receipt_photo} alt="struk" className="h-full w-full object-cover" />
                <button onClick={() => setForm({ ...form, receipt_photo: null })} className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white" data-testid="pc-receipt-remove"><X size={12} /></button>
              </div>
            ) : (
              <label className="flex h-24 w-full cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-line text-xs text-ink-mute hover:border-primary hover:text-primary" data-testid="pc-receipt-upload">
                <Wallet size={18} /> Unggah struk
                <input type="file" accept="image/*" className="hidden" onChange={onReceipt} />
              </label>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setModal(null)}>Batal</Button>
            <Button onClick={saveExpense} disabled={busy} data-testid="pc-form-save">{busy ? "Menyimpan…" : "Simpan"}</Button>
          </div>
        </div>
      </Modal>

      {/* Top up modal */}
      <Modal open={modal === "topup"} onClose={() => setModal(null)} title="Cash In — Petty Cash">
        <div className="space-y-3">
          {isOwner && (
            <Select label="Cabang" value={topForm.branch_id} onChange={(e) => setTopForm({ ...topForm, branch_id: e.target.value })} data-testid="pc-top-branch">
              <option value="">Pilih cabang</option>
              {wallets.map((w) => <option key={w.branch_id} value={w.branch_id}>{w.branch_name}</option>)}
            </Select>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Input label="Tanggal" type="date" value={topForm.date} onChange={(e) => setTopForm({ ...topForm, date: e.target.value })} data-testid="pc-top-date" />
            <Input label="Jumlah (Rp)" type="number" min="0" value={topForm.amount} onChange={(e) => setTopForm({ ...topForm, amount: e.target.value })} data-testid="pc-top-amount" />
          </div>
          <Select label="Sumber Cash In" value={topForm.source}
            onChange={(e) => setTopForm({ ...topForm, source: e.target.value })}
            data-testid="pc-top-source">
            <option value="">— Pilih sumber (wajib) —</option>
            {TOPUP_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
          <Input label="Catatan (opsional)" value={topForm.note} onChange={(e) => setTopForm({ ...topForm, note: e.target.value })} data-testid="pc-top-note" />
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => setModal(null)}>Batal</Button>
            <Button onClick={saveTopup} disabled={busy} data-testid="pc-top-save">{busy ? "Menyimpan…" : "Simpan Cash In"}</Button>
          </div>
        </div>
      </Modal>

      {/* Reject modal */}
      <Modal open={modal === "reject"} onClose={() => setModal(null)} title="Tolak Cash Out">
        <div className="space-y-3">
          <p className="text-sm text-ink-soft">Menolak akan mengembalikan saldo petty cash (transaksi dikeluarkan dari perhitungan).</p>
          <Input label="Alasan (opsional)" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="pc-reject-reason" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setModal(null)}>Batal</Button>
            <Button onClick={doReject} disabled={busy} data-testid="pc-reject-confirm">{busy ? "Memproses…" : "Tolak"}</Button>
          </div>
        </div>
      </Modal>

      {/* Detail modal */}
      <Modal open={modal === "detail"} onClose={() => setModal(null)} title="Detail Transaksi">
        {detail && (
          <div className="space-y-2 text-sm" data-testid="pc-detail">
            <Row k="No. Dokumen" v={<span className="font-mono font-semibold">{detail.doc_number}</span>} />
            <Row k="Status" v={<Badge tone={(STATUS[detail.status] || STATUS.pending).tone}>{(STATUS[detail.status] || STATUS.pending).label}</Badge>} />
            <Row k="Tanggal" v={detail.date} />
            <Row k="Cabang" v={detail.branch_name} />
            <Row k="Kategori" v={detail.category_name} />
            <Row k="Jumlah" v={<b>{rp(detail.amount)}</b>} />
            <Row k="Deskripsi" v={detail.description} />
            <Row k="Dibuat oleh" v={`${detail.created_by_name || "-"} · ${(detail.created_at || "").slice(0, 16).replace("T", " ")}`} />
            {detail.approved_by_name && <Row k="Disetujui oleh" v={`${detail.approved_by_name} · ${(detail.approved_at || "").slice(0, 16).replace("T", " ")}`} />}
            {detail.status === "rejected" && <Row k="Ditolak oleh" v={`${detail.rejected_by_name || "-"}${detail.reject_reason ? ` — ${detail.reject_reason}` : ""}`} />}
            {detail.receipt_photo && <div><p className="mb-1 text-xs font-semibold text-ink-soft">Struk</p><img src={detail.receipt_photo} alt="struk" className="max-h-72 rounded-xl border border-line" /></div>}
            <JournalPreview module="petty_cash" docId={detail.id} className="!mt-3" />
          </div>
        )}
      </Modal>
    </div>
  );
}

const Stat = ({ label, value, tone, strong, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs text-ink-mute">{label}</p>
    <p className={`mt-0.5 font-heading font-bold ${strong ? "text-lg text-primary" : "text-base"} ${tone === "success" ? "text-emerald-600" : tone === "danger" ? "text-danger" : "text-ink"}`}>{value}</p>
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-4 border-b border-line/40 py-1.5">
    <span className="text-ink-mute">{k}</span>
    <span className="text-right text-ink">{v}</span>
  </div>
);
